/**
 * Scroll To With Sticky Offset
 * A replacement for scrollIntoView that accounts for sticky headers on iOS.
 * On non-iOS devices, uses standard scrollIntoView for native behavior.
 */

import { isIOS } from './isIOS';

/**
 * Scroll to an element, accounting for sticky header offset on iOS
 * @param {HTMLElement} el - The element to scroll to
 * @param {Object} options - Scroll options
 * @param {string} options.behavior - 'smooth' or 'auto' (default: 'smooth')
 * @param {string} options.block - 'start', 'center', 'end', 'nearest' (default: 'start')
 */
export function scrollToWithStickyOffset(el, { behavior = 'smooth', block = 'start' } = {}) {
  if (!el) return;

  // On non-iOS, use standard scrollIntoView
  if (!isIOS()) {
    el.scrollIntoView({ block, behavior });
    return;
  }

  // On iOS, calculate position manually to account for sticky offset
  const offset = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--sticky-offset'),
    10
  ) || 0;

  const rect = el.getBoundingClientRect();
  let targetY;

  switch (block) {
    case 'center':
      targetY = rect.top + window.pageYOffset - (window.innerHeight / 2) + (rect.height / 2);
      break;
    case 'end':
      targetY = rect.bottom + window.pageYOffset - window.innerHeight;
      break;
    case 'nearest':
      // For nearest, check if element is above or below viewport
      if (rect.top < offset) {
        targetY = rect.top + window.pageYOffset - offset;
      } else if (rect.bottom > window.innerHeight) {
        targetY = rect.bottom + window.pageYOffset - window.innerHeight;
      } else {
        return; // Already visible, no scroll needed
      }
      break;
    case 'start':
    default:
      targetY = rect.top + window.pageYOffset - offset;
      break;
  }

  window.scrollTo({
    top: Math.max(0, targetY),
    behavior
  });
}

export default scrollToWithStickyOffset;
